import React from 'react';
import i18next from 'i18next';
import {Observable} from 'rxjs';

import {combineEpics} from 'web-app/util/redux-observable';
import * as InvoiceApi from 'web-app/api/invoice-api';
import * as AccountItemsApi from 'web-app/react/entities/account-items/api';
import InvoiceBehaviors from 'web-app/behaviors/invoice-behavior-ids';
import {showDialogObservable, showAlertObservable, DialogChoice} from 'web-app/react/dialog';
import * as AccountItemMoveModalActions from 'web-app/react/modals-container/account-item-move/actions';
import AccountItem from 'web-app/react/entities/account-items/model';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {InvoiceMode} from 'web-app/util/graphql/api-types';
import Markdown from 'web-app/react/components/markdown/markdown';

import * as Actions from './actions';

const resendInvoice = action$ =>
    action$
        .ofType(Actions.resend.type)
        .mergeMap(({payload}) =>
            showDialogObservable({
                title: i18next.t('childProfile.accountBalance.confirmResendTitle'),
                description: i18next.t('childProfile.accountBalance.confirmResendDescription'),
                okText: i18next.t('childProfile.accountBalance.reSendInvoice'),
            }).map(selection => ({payload, selection})),
        )
        .mergeMap(({payload, selection}) => {
            if (selection !== DialogChoice.OK) {
                return Observable.of(Actions.resendAborted.action());
            } else {
                return Observable.fromPromise(
                    InvoiceApi.resendInvoice(payload.institutionId, payload.billPayerId, payload.invoiceId),
                )
                    .flatMapTo([
                        Actions.resendSuccess.action(),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('childProfile.accountBalance.resendInvoiceSuccessful'),
                        ),
                    ])
                    .catch(e => Observable.of(Actions.resendFailed.action(e)));
            }
        });

const cancelInvoice = action$ =>
    action$.ofType(Actions.cancel.type).mergeMap(({payload}) => {
        return Observable.fromPromise(
            InvoiceApi.cancelInvoice(
                payload.institutionId,
                payload.billPayerId,
                payload.invoiceId,
                payload.notify,
                payload.creditNoteDate,
            ),
        )
            .map(reply => Actions.cancelSuccess.action(payload.institutionId, payload.billPayerId, reply.invoices))
            .catch(e => Observable.of(Actions.cancelFailed.action(e)));
    });

const moveInvoice = action$ =>
    action$.ofType(Actions.showMoveModal.type).mergeMap(({payload}) => {
        const behavior = payload.invoiceBehaviors.behaviorForId(InvoiceBehaviors.CanMoveInvoice);

        const errors = behavior.payload.warnings.filter(w => w.severity === 'ERROR');
        const warnings = behavior.payload.warnings.filter(w => w.severity === 'WARNING');

        const errorTexts = errors.map(e => e.title);
        const warningTexts = warnings.map(w => w.title);

        const show = Observable.of(
            AccountItemMoveModalActions.show.action(
                payload.institutionId,
                payload.billPayerId,
                AccountItem.Constants.Invoice,
                payload.invoiceId,
            ),
        );

        if (errors.length > 0) {
            return showAlertObservable({
                title: '',
                description: React.createElement(Markdown, {source: errorTexts.join('\n  \n  ')}),
            }).mergeMap(() => Observable.empty());
        } else {
            return warnings.length > 0
                ? showDialogObservable({
                      title: i18next.t('accountItems.move.transferInvoiceWarningTitle'),
                      description: React.createElement(Markdown, {
                          source: i18next.t('accountItems.move.transferInvoiceWarningDescription', {
                              warnings: warningTexts.join('\n  \n  '),
                          }),
                      }),
                  }).mergeMap(selection => (selection === DialogChoice.OK ? show : Observable.empty()))
                : show;
        }
    });

const fetchSingle = action$ =>
    action$.ofType(Actions.fetchSingle.type).mergeMap(({payload}) => {
        return InvoiceApi.fetchSingleInvoice(payload.invoiceId)
            .map(response => {
                return Actions.fetchSingleSuccess.action(response.invoice);
            })
            .catch(e => Observable.of(Actions.fetchSingleFailed.action(e)));
    });

// loadDetailsData is used to fetch details for an invoice that is in a draft state, fx from batch invoicing
// Since the invoice doesn't have an ID we use the invoice period that the invoice has been generated with
// For invoice previews we fetch the data on open since then the invoicePeriod doesn't have to be stored in the state
const loadDetailsData = action$ =>
    action$
        .ofType(Actions.showPreview.type)
        .filter(({payload}) => !payload.useGraphQl && payload.invoicePeriod)
        .mergeMap(({payload}) => {
            return AccountItemsApi.previewInvoice(payload.institutionId, payload.billPayerId, payload.invoicePeriod)
                .map(response => {
                    return Actions.showDetailsSuccess.action(response.details);
                })
                .catch(e => Observable.of(Actions.showDetailsFailed.action(e)));
        });

const convertFormDataToGraphQlInvoiceVariables = (invoiceData, billPayerId) => {
    return {
        requests: {
            billPayerId,
        },
        range: {
            from: invoiceData.from,
            to: invoiceData.to,
        },
        mode: InvoiceMode.Preview,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.due,
        notify: invoiceData.notify,
    };
};

// Since we cannot load the pdf in batch invoicing when doing graphql invoicing
// we have to refetch the invoice when previewing
const loadGraphQLInvoice = action$ =>
    action$
        .ofType(Actions.showPreview.type)
        .filter(({payload}) => payload.useGraphQl && payload.invoicePeriod)
        .mergeMap(({payload}) => {
            return AccountItemsApi.createInvoiceInApollo(
                convertFormDataToGraphQlInvoiceVariables(payload.invoicePeriod, payload.billPayerId),
            )
                .map(response => {
                    return Actions.fetchGraphQLPreviewSuccess.action(response.invoices.first());
                })
                .catch(e => Observable.of(Actions.fetchGraphQLPreviewFailed.action(e)));
        });

// showDetails is used to fetch details for an invoice that has been sent as therefore has an invoiceId
// For sent invoices we fetch the data when the user clicks details,
// because the user might not have permission to see the details
const showDetails = action$ =>
    action$
        .ofType(Actions.showDetails.type)
        .filter(({payload}) => payload.show && !payload.hasDetails)
        .mergeMap(({payload}) => {
            return InvoiceApi.fetchDetails(payload.invoiceId)
                .map(response => {
                    return Actions.showDetailsSuccess.action(response.children);
                })
                .catch(e => Observable.of(Actions.showDetailsFailed.action(e)));
        });

export default combineEpics(
    showDetails,
    fetchSingle,
    resendInvoice,
    cancelInvoice,
    loadDetailsData,
    loadGraphQLInvoice,
    moveInvoice,
);
